import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../foundation/altzero_base/base/components/ui/card";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../foundation/altzero_base/base/components/ui/tabs";
import { Mail, Plus, Send, CheckCircle2, AlertCircle, Inbox, Users, ArrowRight } from "lucide-react";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import { useDocumentForm } from "../contexts/DocumentFormContext";
import { EmailAccountManager } from "./email/EmailAccountManager";
import { EmailCompositionDialog } from "./email/EmailCompositionDialog";
import { RetailerCommunicationHub } from "./email/RetailerCommunicationHub";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

interface RetailerCommunicationStepProps {
    onNext: () => void;
    onPrevious: () => void;
}

const RetailerCommunicationStep: React.FC<RetailerCommunicationStepProps> = ({ onNext, onPrevious }) => {
    const { toast } = useToast();
    const { tenderId, tenderName, sites, tenderDetails } = useDocumentForm();
    const [activeTab, setActiveTab] = useState<string>("setup");
    const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
    const [emailAccount, setEmailAccount] = useState<any>(null);
    const [emailStats, setEmailStats] = useState({ sent: 0, responses: 0 });
    const [statsRefreshKey, setStatsRefreshKey] = useState(0);

    useEffect(() => {
        checkEmailAccount();
    }, []);

    // Re-fetch stats whenever tenderId changes, when we switch to the send tab,
    // or when an assignment triggers a manual refresh (statsRefreshKey bump)
    useEffect(() => {
        if (!tenderId) return;
        supabase
            .from('tender')
            .select('retailers_sent_count, retailers_replied_count')
            .eq('id', tenderId)
            .single()
            .then(({ data }) => {
                if (data) setEmailStats({
                    sent: data.retailers_sent_count ?? 0,
                    responses: data.retailers_replied_count ?? 0,
                });
            });
    }, [tenderId, statsRefreshKey]);

    // Also re-fetch when user switches back to the send tab (so the count is fresh)
    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        if (tab === 'send') setStatsRefreshKey(k => k + 1);
    };

    // Called when an unlinked email is assigned — refreshes the stats counter
    const handleEmailAssigned = () => {
        setStatsRefreshKey(k => k + 1);
    };

    const checkEmailAccount = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const userId = session?.user?.id;

            const response = await fetch('/api/scopingai/email/accounts', {
                headers: { 'x-user-id': userId || '' }
            });
            if (response.ok) {
                const accounts = await response.json();
                if (accounts.length > 0) {
                    setEmailAccount(accounts[0]);
                    setActiveTab("send");
                }
            }
        } catch (error) {
            console.error("Error checking email account:", error);
        }
    };

    const handleSkip = () => {
        toast({
            title: "Email Communication Skipped",
            description: "You can send emails from the tender dashboard later.",
        });
        onNext();
    };

    const handleContinue = () => {
        onNext();
    };

    return (
        <div className="space-y-6">
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50/50 to-white">
                <CardHeader>
                    <div className="flex items-start justify-between">
                        <div>
                            <CardTitle className="text-2xl text-blue-900 flex items-center gap-2">
                                <Mail className="w-6 h-6 text-blue-600" />
                                Retailer Communication
                            </CardTitle>
                            <CardDescription className="mt-2 text-base">
                                Connect your email and send Request for Information (RFI) to energy retailers
                            </CardDescription>
                        </div>
                        <Badge variant="outline" className="bg-white border-blue-200 text-blue-700">
                            <Users className="w-3 h-3 mr-1" />
                            Step 2
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent>
                    {emailAccount ? (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="font-medium text-green-900">Email Account Connected</p>
                                <p className="text-sm text-green-700">{emailAccount.email}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="font-medium text-amber-900">No Email Account Connected</p>
                                <p className="text-sm text-amber-700">Connect your Gmail account to send RFI emails to retailers</p>
                            </div>
                        </div>
                    )}

                    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                        <TabsList className="grid w-full grid-cols-3 mb-6">
                            <TabsTrigger value="setup" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                                <Mail className="w-4 h-4 mr-2" />
                                Setup
                            </TabsTrigger>
                            <TabsTrigger value="send" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" disabled={!emailAccount}>
                                <Send className="w-4 h-4 mr-2" />
                                Send RFI
                            </TabsTrigger>
                            <TabsTrigger value="inbox" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" disabled={!emailAccount}>
                                <Inbox className="w-4 h-4 mr-2" />
                                Inbox
                            </TabsTrigger>
                        </TabsList>

                        {/* Setup Tab */}
                        <TabsContent value="setup" className="space-y-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Connect Email Account</CardTitle>
                                    <CardDescription>
                                        Connect your Gmail account to send and receive emails with retailers
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <EmailAccountManager />
                                </CardContent>
                            </Card>

                            <div className="flex items-center justify-between pt-4 border-t">
                                <Button variant="outline" onClick={onPrevious} className="border-gray-300">
                                    Back to Tender Details
                                </Button>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={handleSkip}
                                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                    >
                                        Skip for Now
                                    </Button>
                                    {emailAccount && (
                                        <Button onClick={() => setActiveTab("send")} className="bg-blue-600 hover:bg-blue-700">
                                            Continue to Send RFI
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </TabsContent>

                        {/* Send RFI Tab */}
                        <TabsContent value="send" className="space-y-4">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <CardTitle className="text-lg">Send Request for Information</CardTitle>
                                            <CardDescription>
                                                Send RFI emails to energy retailers for tender: <strong>{tenderName}</strong>
                                            </CardDescription>
                                        </div>
                                        <Button
                                            onClick={() => setIsEmailDialogOpen(true)}
                                            className="bg-blue-600 hover:bg-blue-700"
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Compose Email
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <Card className="bg-blue-50/50">
                                                <CardContent className="pt-6">
                                                    <div className="text-center">
                                                        <div className="text-3xl font-bold text-blue-700">{sites?.length || 0}</div>
                                                        <div className="text-sm text-gray-600 mt-1">Sites in Tender</div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                            <Card className="bg-green-50/50">
                                                <CardContent className="pt-6">
                                                    <div className="text-center">
                                                        <div className="text-3xl font-bold text-green-700">{emailStats.sent}</div>
                                                        <div className="text-sm text-gray-600 mt-1">Emails Sent</div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                            <Card className="bg-amber-50/50">
                                                <CardContent className="pt-6">
                                                    <div className="text-center">
                                                        <div className="text-3xl font-bold text-amber-700">{emailStats.responses}</div>
                                                        <div className="text-sm text-gray-600 mt-1">Responses Received</div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </div>

                                        <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
                                            <h3 className="font-semibold text-gray-900 mb-3">Quick Tips for RFI Emails</h3>
                                            <ul className="space-y-2 text-sm text-gray-700">
                                                <li className="flex items-start gap-2">
                                                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                    <span>Include all site details and NMI numbers in your email</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                    <span>Specify your timeframe for receiving quotes</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                    <span>Attach any relevant tender documents if available</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                    <span>Check the Inbox tab to track replies from retailers</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <div className="flex items-center justify-between pt-4 border-t">
                                <Button variant="outline" onClick={() => setActiveTab("setup")} className="border-gray-300">
                                    Back to Setup
                                </Button>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => setActiveTab("inbox")}
                                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                    >
                                        Check Inbox
                                    </Button>
                                    <Button onClick={handleContinue} className="bg-blue-600 hover:bg-blue-700">
                                        Continue to Offers
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </div>
                        </TabsContent>

                        {/* Inbox Tab */}
                        <TabsContent value="inbox" className="space-y-4">
                            <Card className="flex flex-col h-[800px] overflow-hidden border-0 shadow-none ring-1 ring-gray-200">
                                <CardHeader className="px-6 py-4 border-b bg-white shrink-0">
                                    <CardTitle className="text-lg">Email Inbox & Responses</CardTitle>
                                    <CardDescription>
                                        View and manage email conversations with retailers
                                    </CardDescription>
                                </CardHeader>
                                <div className="flex-1 overflow-hidden bg-gray-50">
                                    {tenderId ? (
                                        <div className="h-full w-full">
                                            <RetailerCommunicationHub tenderId={tenderId} onEmailAssigned={handleEmailAssigned} />
                                        </div>
                                    ) : (
                                        <div className="h-full flex flex-col items-center justify-center text-gray-500">
                                            <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                                            <p>Save your tender details first to view email threads</p>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            <div className="flex items-center justify-between pt-4 border-t">
                                <Button variant="outline" onClick={() => setActiveTab("send")} className="border-gray-300">
                                    Back to Send RFI
                                </Button>
                                <Button onClick={handleContinue} className="bg-blue-600 hover:bg-blue-700">
                                    Continue to Offers
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </div>
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>

            {tenderId && (
                <EmailCompositionDialog
                    isOpen={isEmailDialogOpen}
                    onClose={() => setIsEmailDialogOpen(false)}
                    tenderId={tenderId}
                    tenderName={tenderName}
                    tenderNotes={sites[0]?.retailerNotes || tenderDetails.notes || ''}
                    sites={sites}
                />
            )}
        </div>
    );
};

export default RetailerCommunicationStep;
